/**
 * useWizardMachine Hook
 *
 * Simplified hook that wraps the XState wizard machine.
 *
 * Key design:
 * - NO useEffects for state synchronization
 * - Machine controls all async operations (fetch, save, poll)
 * - Data comes from machine context
 * - All save/navigation uses unified OPERATING flow
 */

import { useMemo, useCallback } from 'react'
import { useMachine } from '@xstate/react'
import { wizardMachine } from './wizardMachine'
import {
  WizardStep,
  InternalState,
  NestedState,
  EventType,
  OperationType,
  NavigationDirection,
  OperatingPhase,
  getOperationMessage,
} from './types'
import { ImportData } from '../importer-types'

export function useWizardMachine(importNumber: number) {
  const [state, send] = useMachine(wizardMachine, {
    input: { importNumber },
  })

  // ============ Derived Values from State ============

  const currentStep = useMemo((): WizardStep | null => {
    if (state.matches(WizardStep.STEP1_CONFIRM)) return WizardStep.STEP1_CONFIRM
    if (state.matches(WizardStep.STEP2_REVIEW)) return WizardStep.STEP2_REVIEW
    if (state.matches(WizardStep.STEP3_DECIDE)) return WizardStep.STEP3_DECIDE
    if (state.matches(WizardStep.STEP4_IMPORT)) return WizardStep.STEP4_IMPORT
    return null
  }, [state])

  const canGoBack = useMemo(() => {
    if (!currentStep) return false
    const availableSteps = state.context.availableSteps
    const currentIndex = availableSteps.indexOf(currentStep)
    return currentIndex > 0
  }, [currentStep, state.context.availableSteps])

  const canGoNext = useMemo(() => {
    if (!currentStep) return false
    const availableSteps = state.context.availableSteps
    const currentIndex = availableSteps.indexOf(currentStep)
    return currentIndex < availableSteps.length - 1
  }, [currentStep, state.context.availableSteps])

  // ============ Loading States ============

  const isInitializing = state.matches(InternalState.INITIALIZING)
  const isWaitingForProcess = state.matches(InternalState.WAITING_FOR_PROCESS)
  const isError = state.matches(InternalState.ERROR)

  // OPERATING state checks
  const isOperating = state.matches(InternalState.OPERATING)
  const isOperatingSaving = state.matches({
    [InternalState.OPERATING]: OperatingPhase.SAVING,
  })

  // Combined loading state
  const isLoading = isInitializing || isWaitingForProcess || isOperating

  // Modal states
  const isDeselectModalOpen = state.matches({
    [WizardStep.STEP1_CONFIRM]: NestedState.DESELECT_MODAL,
  })
  const isConfirmModalOpen = state.matches({
    [WizardStep.STEP3_DECIDE]: NestedState.CONFIRM_MODAL,
  })

  // Deal validation states
  const isValidatingDeals = state.matches({
    [WizardStep.STEP4_IMPORT]: NestedState.VALIDATING_DEALS,
  })
  const isDealProductModalOpen = state.matches({
    [WizardStep.STEP4_IMPORT]: NestedState.DEAL_PRODUCT_MODAL,
  })
  const isDealValidationModalOpen = state.matches({
    [WizardStep.STEP4_IMPORT]: NestedState.DEAL_VALIDATION_MODAL,
  })

  // ============ Loading Message ============

  const loadingMessage = useMemo(() => {
    if (isInitializing) return 'Loading import data...'
    if (isWaitingForProcess) return 'Processing...'
    if (isValidatingDeals) return 'Validating deals...'
    if (isOperating) {
      return getOperationMessage(state.context.operation)
    }
    return ''
  }, [
    isInitializing,
    isWaitingForProcess,
    isValidatingDeals,
    isOperating,
    state.context.operation,
  ])

  // ============ Core Operations ============

  /**
   * Start an operation using the unified OPERATING flow.
   */
  const startOperation = useCallback(
    (
      operation: OperationType,
      direction: NavigationDirection = 'stay',
      data?: ImportData
    ) => {
      if (!currentStep) return
      send({
        type: EventType.START_OPERATION,
        operation,
        originStep: currentStep,
        direction,
        data,
      })
    },
    [send, currentStep]
  )

  // ============ Navigation ============

  /**
   * Step-aware "Next" button. The machine handles per-step behavior:
   * - Step 1 with deselections → deselect modal
   * - Step 1 without deselections → navigate forward
   * - Step 2 → navigate forward
   * - Step 3 → set-companies operation
   */
  const next = useCallback(
    (data?: ImportData, deselectedHashes?: string[]) => {
      send({ type: EventType.NEXT, data, deselectedHashes })
    },
    [send]
  )

  /** Navigate backward to previous step (saves data first) */
  const navigateBack = useCallback(
    (data?: ImportData) => {
      startOperation(OperationType.NAVIGATE, 'back', data)
    },
    [startOperation]
  )

  /** Save and stay on current step */
  const save = useCallback(
    (data?: ImportData) => {
      startOperation(OperationType.NAVIGATE, 'stay', data)
    },
    [startOperation]
  )

  // ============ Import Operations ============

  /** Import tags (calls set-tags API) */
  const importTags = useCallback(() => {
    startOperation(OperationType.SET_TAGS)
  }, [startOperation])

  /** Import contacts (calls set-contacts API) */
  const importContacts = useCallback(() => {
    startOperation(OperationType.SET_CONTACTS)
  }, [startOperation])

  /** Import notes (calls set-company-notes API) */
  const importNotes = useCallback(() => {
    startOperation(OperationType.SET_NOTES)
  }, [startOperation])

  // ============ Modal Controls ============

  // Step 1: Deselect modal (machine opens it on NEXT with deselections)
  const confirmDeselect = useCallback(() => {
    send({ type: EventType.CONFIRM_DESELECT })
  }, [send])

  const cancelDeselect = useCallback(() => {
    send({ type: EventType.CANCEL_DESELECT })
  }, [send])

  // Step 3: Confirm matches modal (machine opens it on NEXT)
  const confirmMatches = useCallback(() => {
    send({ type: EventType.CONFIRM_MATCHES })
  }, [send])

  const cancelConfirm = useCallback(() => {
    send({ type: EventType.CANCEL_CONFIRM })
  }, [send])

  // ============ Deal Import ============

  /** Import deals (validates first, then imports) */
  const importDeals = useCallback(() => {
    send({ type: EventType.VALIDATE_DEALS })
  }, [send])

  const cancelDealValidation = useCallback(() => {
    send({ type: EventType.CANCEL_DEAL_VALIDATION })
  }, [send])

  const selectDealProduct = useCallback(
    (productName: string) => {
      send({ type: EventType.DEAL_PRODUCT_SELECTED, productName })
    },
    [send]
  )

  const fixDealValidation = useCallback(
    (
      changes: Array<{ original: string; mapped?: string }>,
      columnName: 'deal_product' | 'deal_account_owner' | 'deal_sales_stage'
    ) => {
      send({
        type: EventType.DEALS_VALIDATION_FIXED,
        changes,
        columnName,
      })
    },
    [send]
  )

  // ============ Error Recovery ============

  const retry = useCallback(() => {
    send({ type: EventType.RETRY })
  }, [send])

  // ============ Return ============

  return {
    // Raw XState state (for advanced usage)
    state,
    send,

    // Current step
    currentStep,
    availableSteps: state.context.availableSteps,
    dataVersion: state.context.dataVersion,
    error: state.context.error,

    // Data from machine context
    // Note: Company categorization (companiesPrematched, etc.) comes from ImporterViewModel
    importData: state.context.importData,
    importName: state.context.importName,
    skipMatching: state.context.skipMatching,

    // Validation data
    dealValidationData: state.context.dealValidationData,

    // Current operation (for UI display)
    operation: state.context.operation,

    // Loading states
    isInitializing,
    isWaitingForProcess,
    isLoading,
    isError,
    isValidatingDeals,

    // OPERATING phase states
    isOperating,
    isOperatingSaving,

    // Modal states
    isDeselectModalOpen,
    isConfirmModalOpen,
    isDealProductModalOpen,
    isDealValidationModalOpen,

    // Navigation flags
    canGoBack,
    canGoNext,
    loadingMessage,

    // State matching helper
    matches: state.matches.bind(state),

    // =========================================================================
    // Actions
    // =========================================================================

    // Navigation (all go through OPERATING)
    next,
    navigateBack,
    save,

    // Import operations (all go through OPERATING)
    importTags,
    importContacts,
    importNotes,
    importDeals,

    // Modal controls
    confirmDeselect,
    cancelDeselect,
    confirmMatches,
    cancelConfirm,

    // Deal validation helpers
    cancelDealValidation,
    selectDealProduct,
    fixDealValidation,

    // Error recovery
    retry,
  }
}

// Export type for consumers
export type WizardMachineReturn = ReturnType<typeof useWizardMachine>
